import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  FileText, 
  Plus, 
  Edit3, 
  Trash2, 
  Save, 
  X, 
  ChevronDown, 
  ChevronRight,
  Database,
  ArrowRight,
  CheckCircle,
  AlertTriangle,
  Eye,
  Copy
} from 'lucide-react';

interface ConversionSpec {
  id: string;
  name: string;
  version: string;
  description: string;
  source: {
    type: string;
    connection: Record<string, any>;
    tables: Array<{
      name: string;
      primaryKey: string;
      columns: Array<{
        name: string;
        type: string;
        required: boolean;
      }>;
    }>;
  };
  transformations: Array<{
    name: string;
    sourceTable: string;
    targetTable: string;
    rules: Array<{
      source: string;
      target: string;
      type: string;
    }>;
    validations: Array<{
      field: string;
      type: string;
      values?: string[];
    }>;
  }>;
  sink: {
    type: string;
    config: Record<string, any>;
  };
  processing: {
    batchSize: number;
    maxRetries: number;
    retryDelay: number;
    timeout: number;
    parallelWorkers: number;
    enableCDC: boolean;
    cdcLagThreshold: number;
    enableReplay: boolean;
    replayWindowSize: number;
  };
  quality: {
    enableValidation: boolean;
    enableChecksums: boolean;
    enableParity: boolean;
    parityThreshold: number;
    enableAudit: boolean;
    auditRetention: number;
  };
  monitoring: {
    enableMetrics: boolean;
    metricsInterval: number;
    enableAlerts: boolean;
    alertThresholds: Record<string, number>;
    enableDashboards: boolean;
    dashboardRefresh: number;
  };
  security: {
    enableEncryption: boolean;
    encryptionAlgorithm: string;
    enableAuditLog: boolean;
    auditLogLevel: string;
    enableDataMasking: boolean;
    maskingRules: string[];
  };
  status: 'active' | 'inactive' | 'draft' | 'error';
  lastModified: string;
  createdBy: string;
}

export const ConversionSpecManager: React.FC = () => {
  const [specs, setSpecs] = useState<ConversionSpec[]>([]);
  const [selectedSpec, setSelectedSpec] = useState<ConversionSpec | null>(null);
  const [editingSpec, setEditingSpec] = useState<ConversionSpec | null>(null);
  const [showAddSpec, setShowAddSpec] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['source', 'transformations']));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadSpecs = async () => {
      try {
        setLoading(true);
        // Simulate loading conversion specs
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        setSpecs([
          {
            id: 'spec-1',
            name: 'Legacy Parcel Migration',
            version: 'v1.0',
            description: 'Migration of legacy parcel data to Tyler system',
            source: {
              type: 'postgresql',
              connection: {
                host: 'localhost',
                port: 5432,
                database: 'hivecore',
                schema: 'public'
              },
              tables: [
                {
                  name: 'legacy_parcel',
                  primaryKey: 'parcel_num',
                  columns: [
                    { name: 'parcel_num', type: 'text', required: true },
                    { name: 'addr1', type: 'text', required: true },
                    { name: 'city', type: 'text', required: true },
                    { name: 'state', type: 'text', required: true },
                    { name: 'zip', type: 'text', required: true },
                    { name: 'assessed', type: 'numeric', required: true }
                  ]
                }
              ]
            },
            transformations: [
              {
                name: 'parcel_mapping',
                sourceTable: 'legacy_parcel',
                targetTable: 'tyler_parcel',
                rules: [
                  { source: 'parcel_num', target: 'parcel_id', type: 'direct' },
                  { source: 'addr1', target: 'address_line1', type: 'direct' },
                  { source: 'city', target: 'city_name', type: 'direct' },
                  { source: 'state', target: 'state_code', type: 'direct' },
                  { source: 'zip', target: 'postal_code', type: 'direct' },
                  { source: 'assessed', target: 'assessed_value', type: 'direct' }
                ],
                validations: [
                  { field: 'parcel_num', type: 'not_null' },
                  { field: 'assessed', type: 'positive_number' },
                  { field: 'state', type: 'state_code' }
                ]
              }
            ],
            sink: {
              type: 'tyler_csv',
              config: {
                outputPath: '/tmp/conversionops/tyler/csv',
                batchSize: 100,
                includeHeaders: true
              }
            },
            processing: {
              batchSize: 1000,
              maxRetries: 3,
              retryDelay: 5000,
              timeout: 300000,
              parallelWorkers: 4,
              enableCDC: true,
              cdcLagThreshold: 5000,
              enableReplay: true,
              replayWindowSize: 3600000
            },
            quality: {
              enableValidation: true,
              enableChecksums: true,
              enableParity: true,
              parityThreshold: 100.0,
              enableAudit: true,
              auditRetention: 2592000000
            },
            monitoring: {
              enableMetrics: true,
              metricsInterval: 60000,
              enableAlerts: true,
              alertThresholds: {
                errorRate: 0.01,
                latencyP95: 5000,
                latencyP99: 10000,
                cdcLag: 10000
              },
              enableDashboards: true,
              dashboardRefresh: 30000
            },
            security: {
              enableEncryption: true,
              encryptionAlgorithm: 'AES-256',
              enableAuditLog: true,
              auditLogLevel: 'INFO',
              enableDataMasking: false,
              maskingRules: []
            },
            status: 'active',
            lastModified: '2025-01-01T12:00:00Z',
            createdBy: 'engineer@hivecore.com'
          }
        ]);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load conversion specs');
      } finally {
        setLoading(false);
      }
    };

    loadSpecs();
  }, []);

  const toggleSection = (section: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(section)) {
      newExpanded.delete(section);
    } else {
      newExpanded.add(section);
    }
    setExpandedSections(newExpanded);
  };

  const handleSpecAction = async (specId: string, action: 'start' | 'stop' | 'test' | 'copy') => {
    console.log(`Performing ${action} on spec ${specId}`);
    // In real implementation, this would call the appropriate API
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <CheckCircle size={16} className="text-green-400" />;
      case 'inactive':
        return <X size={16} className="text-slate-400" />;
      case 'draft':
        return <Edit3 size={16} className="text-yellow-400" />;
      case 'error':
        return <AlertTriangle size={16} className="text-red-400" />;
      default:
        return <X size={16} className="text-slate-400" />;
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-32 bg-slate-800/50 rounded-lg animate-pulse" data-testid="skeleton-placeholder" />
        <div className="h-96 bg-slate-800/50 rounded-lg animate-pulse" data-testid="skeleton-placeholder" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <AlertTriangle size={48} className="text-red-400 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-white mb-2">Failed to load conversion specs</h3>
        <p className="text-slate-400 mb-4">{error}</p>
        <button 
          onClick={() => window.location.reload()} 
          className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-white transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Conversion Specs List */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="evolut-metric-card p-6"
      >
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-white">Conversion Specifications</h3>
          <button
            onClick={() => setShowAddSpec(true)}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-white transition-colors flex items-center space-x-2"
          >
            <Plus size={16} />
            <span>New Spec</span>
          </button>
        </div>

        <div className="space-y-4">
          {specs.map((spec) => (
            <motion.div
              key={spec.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="p-4 bg-slate-800/50 rounded-lg border border-slate-700/50"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <FileText size={20} className="text-blue-400" />
                  <div>
                    <h4 className="text-white font-medium">{spec.name}</h4>
                    <p className="text-sm text-slate-400">
                      {spec.version} • {spec.description}
                    </p>
                    <p className="text-xs text-slate-500">
                      Modified: {new Date(spec.lastModified).toLocaleDateString()} by {spec.createdBy}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-2">
                    {getStatusIcon(spec.status)}
                    <span className="text-sm text-slate-400 capitalize">{spec.status}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setSelectedSpec(spec)}
                      className="p-2 hover:bg-slate-700/50 rounded-lg transition-colors"
                      title="View Details"
                    >
                      <Eye size={16} className="text-blue-400" />
                    </button>
                    <button
                      onClick={() => setEditingSpec(spec)}
                      className="p-2 hover:bg-slate-700/50 rounded-lg transition-colors"
                      title="Edit Spec"
                    >
                      <Edit3 size={16} className="text-yellow-400" />
                    </button>
                    <button
                      onClick={() => handleSpecAction(spec.id, 'copy')}
                      className="p-2 hover:bg-slate-700/50 rounded-lg transition-colors"
                      title="Copy Spec"
                    >
                      <Copy size={16} className="text-green-400" />
                    </button>
                    <button
                      onClick={() => handleSpecAction(spec.id, 'test')}
                      className="p-2 hover:bg-slate-700/50 rounded-lg transition-colors"
                      title="Test Spec"
                    >
                      <CheckCircle size={16} className="text-purple-400" />
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Spec Details */}
      {selectedSpec && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="evolut-metric-card p-6"
        >
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-white">Specification Details</h3>
            <button
              onClick={() => setSelectedSpec(null)}
              className="p-2 hover:bg-slate-700/50 rounded-lg transition-colors"
            >
              <X size={20} className="text-slate-400" />
            </button>
          </div>

          <div className="space-y-6">
            {/* Source Configuration */}
            <div>
              <button
                onClick={() => toggleSection('source')}
                className="flex items-center space-x-2 text-white font-medium mb-4"
              >
                {expandedSections.has('source') ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                <Database size={20} className="text-blue-400" />
                <span>Source Configuration</span>
              </button>
              {expandedSections.has('source') && (
                <div className="ml-8 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-400 mb-2">Type</label>
                      <div className="px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white">
                        {selectedSpec.source.type}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-400 mb-2">Database</label>
                      <div className="px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white">
                        {selectedSpec.source.connection.database}
                      </div>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-400 mb-2">Tables</label>
                    <div className="space-y-2">
                      {selectedSpec.source.tables.map((table, index) => (
                        <div key={index} className="p-3 bg-slate-800/50 rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-white font-medium">{table.name}</span>
                            <span className="text-sm text-slate-400">PK: {table.primaryKey}</span>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                            {table.columns.map((column, colIndex) => (
                              <div key={colIndex} className="text-sm">
                                <span className="text-white">{column.name}</span>
                                <span className="text-slate-400 ml-2">({column.type})</span>
                                {column.required && <span className="text-red-400 ml-1">*</span>}
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Transformations */}
            <div>
              <button
                onClick={() => toggleSection('transformations')}
                className="flex items-center space-x-2 text-white font-medium mb-4"
              >
                {expandedSections.has('transformations') ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                <ArrowRight size={20} className="text-green-400" />
                <span>Transformations</span>
              </button>
              {expandedSections.has('transformations') && (
                <div className="ml-8 space-y-4">
                  {selectedSpec.transformations.map((transformation, index) => (
                    <div key={index} className="p-4 bg-slate-800/50 rounded-lg">
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="text-white font-medium">{transformation.name}</h4>
                        <div className="flex items-center space-x-4 text-sm text-slate-400">
                          <span>{transformation.sourceTable} → {transformation.targetTable}</span>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-slate-400 mb-2">Mapping Rules</label>
                          <div className="space-y-2">
                            {transformation.rules.map((rule, ruleIndex) => (
                              <div key={ruleIndex} className="flex items-center space-x-2 text-sm">
                                <span className="text-white">{rule.source}</span>
                                <ArrowRight size={14} className="text-slate-400" />
                                <span className="text-white">{rule.target}</span>
                                <span className="text-slate-400">({rule.type})</span>
                              </div>
                            ))}
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-400 mb-2">Validations</label>
                          <div className="space-y-2">
                            {transformation.validations.map((validation, valIndex) => (
                              <div key={valIndex} className="text-sm">
                                <span className="text-white">{validation.field}</span>
                                <span className="text-slate-400 ml-2">({validation.type})</span>
                                {validation.values && (
                                  <span className="text-slate-400 ml-2">
                                    [{validation.values.join(', ')}]
                                  </span>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Processing Configuration */}
            <div>
              <button
                onClick={() => toggleSection('processing')}
                className="flex items-center space-x-2 text-white font-medium mb-4"
              >
                {expandedSections.has('processing') ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                <Database size={20} className="text-purple-400" />
                <span>Processing Configuration</span>
              </button>
              {expandedSections.has('processing') && (
                <div className="ml-8">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-400 mb-2">Batch Size</label>
                      <div className="px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white">
                        {selectedSpec.processing.batchSize.toLocaleString()}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-400 mb-2">Parallel Workers</label>
                      <div className="px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white">
                        {selectedSpec.processing.parallelWorkers}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-400 mb-2">Timeout (ms)</label>
                      <div className="px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white">
                        {selectedSpec.processing.timeout.toLocaleString()}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-400 mb-2">Max Retries</label>
                      <div className="px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white">
                        {selectedSpec.processing.maxRetries}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-400 mb-2">CDC Enabled</label>
                      <div className="px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white">
                        {selectedSpec.processing.enableCDC ? 'Yes' : 'No'}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-400 mb-2">CDC Lag Threshold</label>
                      <div className="px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white">
                        {selectedSpec.processing.cdcLagThreshold}ms
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
};